import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingCTA from '../components/FloatingCTA';
import ImageGalleryModal from '../components/ImageGalleryModal';
import Button from '../components/Button';
import { MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Referenzen = () => {
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Featured project data (project-01)
  const featuredProject = {
    title: "Bauleitung & Projektleitung (Doppelhaus bis 200 WE)",
    location: "diverse Standorte",
    folder: "project-01",
    mainImage: "main.JPG",
    galleryImages: ["gallery-01.JPG", "gallery-02.JPG", "gallery-03.PNG", "gallery-04.PNG", "gallery-05.jpg", "gallery-06.jpg", "gallery-07.jpg", "gallery-08.jpg", "gallery-09.JPG"]
  };

  // Regular projects data (project-02 to project-10)
  const projects = [{
    title: "20 Wohneinheiten + Tiefgarage (schlüsselfertig)",
    location: "München, Hanauerstraße",
    folder: "project-02",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }, {
    title: "Moschee",
    location: "Bobingen",
    folder: "project-03",
    mainImage: "main.JPG",
    galleryImages: ["gallery-01.JPG", "gallery-02.JPG", "gallery-03.JPG"]
  }, {
    title: "Doppelhaushälfte-Sanierung KfW-Effizienzhaus 70 (schlüsselfertig)",
    location: "Eurasburg",
    folder: "project-04",
    mainImage: "main.png",
    galleryImages: ["gallery-01.png", "gallery-02.JPG", "gallery-03.JPG"]
  }, {
    title: "Doppelhaus (schlüsselfertig)",
    location: "München-Zamdorf",
    folder: "project-05",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }, {
    title: "AWO Aufstockung & Fassadensanierung",
    location: "München",
    folder: "project-06",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }, {
    title: "AWO Küchenrenovierung & -sanierung",
    location: "München-Pasing",
    folder: "project-08",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }, {
    title: "Renovierung von 10 AWO-Apartments",
    location: "München-Perlach",
    folder: "project-09",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }, {
    title: "Sichtbeton-Holzhaus",
    location: "Schwabhausen",
    folder: "project-10",
    mainImage: "main.jpg",
    galleryImages: ["gallery-01.jpg", "gallery-02.jpg", "gallery-03.jpg"]
  }];

  // Generate Supabase URL for images
  const getImageUrl = (folder: string, filename: string) => {
    return `https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/references/${folder}/${filename}`;
  };

  // Get all images for a project
  const getProjectImages = (project: typeof projects[0] | typeof featuredProject) => {
    const mainUrl = getImageUrl(project.folder, project.mainImage);
    const galleryUrls = project.galleryImages.map(img => getImageUrl(project.folder, img));
    return [mainUrl, ...galleryUrls];
  };

  // Handle image loading for progressive display
  const handleImageLoad = (url: string) => {
    setLoadedImages(prev => new Set([...prev, url]));
  };

  // Open gallery modal
  const openGallery = (projectIndex: number, imageIndex: number = 0) => {
    setSelectedProject(projectIndex);
    setSelectedImageIndex(imageIndex);
  };
  const closeGallery = () => {
    setSelectedProject(null);
    setSelectedImageIndex(0);
  };

  // Preload all images on mount
  useEffect(() => {
    const preloadImage = (url: string) => {
      const img = new Image();
      img.onload = () => handleImageLoad(url);
      img.onerror = () => console.log('Failed to load:', url);
      img.src = url;
    };

    // Load featured project images
    const featuredMainUrl = getImageUrl(featuredProject.folder, featuredProject.mainImage);
    preloadImage(featuredMainUrl);
    featuredProject.galleryImages.forEach(image => {
      const imageUrl = getImageUrl(featuredProject.folder, image);
      preloadImage(imageUrl);
    });

    // Load all project main images and gallery images
    projects.forEach(project => {
      const mainUrl = getImageUrl(project.folder, project.mainImage);
      preloadImage(mainUrl);
      project.galleryImages.forEach(image => {
        const imageUrl = getImageUrl(project.folder, image);
        preloadImage(imageUrl);
      });
    });
  }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="section-padding bg-primary-brand text-white pt-32">
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Referenzen</h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed opacity-90">
            Unsere besten Referenzen sind unsere Erfahrung, unser Wissen und unser Engagement für die Arbeit.
          </p>
        </div>
      </section>

      {/* Featured Project - Enhanced Side-by-Side Layout */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
            {/* Desktop: Side-by-Side Layout, Mobile: Stacked */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 min-h-[400px] lg:min-h-[500px]">
              
              {/* Left Side - Hero Image (60% width on desktop) */}
              <div className="lg:col-span-3 cursor-pointer group relative overflow-hidden" onClick={() => openGallery(-1, 0)}>
                <div className="relative h-64 md:h-80 lg:h-full w-full">
                  <img src={getImageUrl(featuredProject.folder, featuredProject.mainImage)} alt={featuredProject.title} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" onLoad={() => handleImageLoad(getImageUrl(featuredProject.folder, featuredProject.mainImage))} />
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
              
              {/* Right Side - Compact Content Area (40% width on desktop) */}
              <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-between">
                
                {/* Project Information */}
                <div className="space-y-4 mb-6">
                  <h2 className="text-xl lg:text-2xl xl:text-3xl font-bold text-primary-brand leading-tight">
                    {featuredProject.title}
                  </h2>
                  <p className="text-gray-600 flex items-center text-sm lg:text-base">
                    <MapPin className="w-4 h-4 lg:w-5 lg:h-5 mr-2 text-secondary-brand flex-shrink-0" />
                    {featuredProject.location}
                  </p>
                </div>
                
                {/* Thumbnail Grid - Optimized Layout */}
                <div className="space-y-3">
                  
                  <div className="grid grid-cols-3 gap-2 lg:gap-3">
                    {featuredProject.galleryImages.slice(0, 9).map((image, index) => {
                    const imageUrl = getImageUrl(featuredProject.folder, image);
                    return <div key={index} className="aspect-square overflow-hidden rounded-lg bg-gray-100 cursor-pointer group" onClick={e => {
                      e.stopPropagation();
                      openGallery(-1, index + 1);
                    }}>
                          <img src={imageUrl} alt={`${featuredProject.title} ${index + 2}`} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300" onLoad={() => handleImageLoad(imageUrl)} />
                        </div>;
                  })}
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regular Projects Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => {
            const mainImageUrl = getImageUrl(project.folder, project.mainImage);
            return <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer" onClick={() => openGallery(index, 0)}>
                  <div className="relative overflow-hidden bg-gray-100 h-56">
                    <img src={mainImageUrl} alt={project.title} className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300" onLoad={() => handleImageLoad(mainImageUrl)} />
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-primary-brand mb-4 leading-tight min-h-[3.5rem] flex items-start">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 flex items-center text-sm mb-4">
                      <MapPin className="w-4 h-4 mr-2 text-secondary-brand flex-shrink-0" />
                      {project.location}
                    </p>
                    
                    {/* Thumbnail Preview */}
                    <div className="grid grid-cols-3 gap-2">
                      {project.galleryImages.map((image, thumbIndex) => {
                    const thumbUrl = getImageUrl(project.folder, image);
                    return <div key={thumbIndex} className="aspect-square overflow-hidden rounded bg-gray-100">
                            <img src={thumbUrl} alt={`${project.title} ${thumbIndex + 2}`} className="w-full h-full object-cover object-center hover:scale-110 transition-transform duration-300" onClick={e => {
                        e.stopPropagation();
                        openGallery(index, thumbIndex + 1);
                      }} onLoad={() => handleImageLoad(thumbUrl)} />
                          </div>;
                  })}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Final CTA Section - Exact match to index page */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto space-y-8 fade-in-up">
            <h2 className="text-4xl font-bold text-primary-brand">
              Bereit für Ihr nächstes Bauprojekt?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Lassen Sie uns gemeinsam Ihre Bauträume verwirklichen. Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
            </p>
            <div className="pt-8">
              <Link to="/kontakt">
                <Button variant="primary" size="lg" icon={<ArrowRight className="w-5 h-5" />}>
                  Jetzt Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      <ImageGalleryModal images={selectedProject === -1 ? getProjectImages(featuredProject) : selectedProject !== null ? getProjectImages(projects[selectedProject]) : []} initialIndex={selectedImageIndex} isOpen={selectedProject !== null} onClose={closeGallery} projectTitle={selectedProject === -1 ? featuredProject.title : selectedProject !== null ? projects[selectedProject].title : ''} />

      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default Referenzen;
